import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowLeft } from 'lucide-react';
import PageTransition from '../../components/common/PageTransition';
import SEOHead from '../../components/common/SEOHead';
import SectionHeader from '../../components/common/SectionHeader';
import { projects } from '../../data/projects';

const Projects: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageTransition>
      <SEOHead title={t('art.projects.title')} />
      <div className="space-y-8">
        <Link
          to="/art#projects"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand-amber transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('art.projects.backToArt')}
        </Link>

        <SectionHeader
          title={t('art.projects.title')}
          description={t('art.projects.description')}
          iconName="Briefcase"
          variant="art"
          badge={t('nav.art')}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {projects.map(project => (
            <Link
              key={project.id}
              to={`/art/projects/${project.id}`}
              state={{ from: 'projects' }}
              className="block group"
            >
              <div className="art-card rounded-2xl overflow-hidden h-full hover:border-brand-amber/30 transition-all">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.imageUrl}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <span className="absolute bottom-3 right-3 text-xs font-semibold text-white/80 bg-black/30 px-2 py-1 rounded-md backdrop-blur-sm">
                    {project.date}
                  </span>
                </div>
                <div className="p-6 space-y-4">
                  <div className="space-y-1">
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white group-hover:text-brand-amber transition-colors">
                      {t(`projects.${project.id.replace('project-', '')}.title`, { defaultValue: project.title })}
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {t(project.description, { defaultValue: project.description })}
                    </p>
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {project.tags.map(tag => (
                      <span key={tag} className="px-2 py-1 bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 rounded-md text-xs font-medium text-slate-600 dark:text-slate-300">
                        {t(`tags.${tag}`, { defaultValue: tag })}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-2">
                    <span className="text-sm font-bold text-brand-amber group-hover:underline">
                      {t('art.projects.viewProject')}
                    </span>
                    {project.link && (
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-brand-amber transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t('art.projects.visitProject')} <ArrowUpRight className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PageTransition>
  );
};

export default Projects;
